// Menu Editor sub-tab for the In-Room QR admin panel.
// Owners edit F&B/amenity items + prices without a code deploy.
// All rows built inline (no sub-components with props.map — Babel plugin quirk).
import React, { useCallback, useEffect, useState } from 'react';
import {
  Loader2, RefreshCw, Plus, Trash2, Save, X, ToggleLeft, ToggleRight, Languages, ImagePlus,
} from 'lucide-react';
import { apiCall, API_URL } from './_shared';

const eur = (n) => `€${Number(n || 0).toFixed(2)}`;

const LANGS = ['me', 'sq', 'de', 'ru', 'pl', 'hu'];   // en is the base `name`

const InRoomMenuTab = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  const [savingSku, setSavingSku] = useState(null);
  const [drafts, setDrafts] = useState({}); // sku → {name,price,section,is_active}
  const [showNew, setShowNew] = useState(false);
  const [nu, setNu] = useState({ sku: '', name: '', section: 'beverages', price: 0 });
  const [transDlg, setTransDlg] = useState(null);   // {sku, name, translations}
  const [translating, setTranslating] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(null);
  const [transMsg, setTransMsg] = useState('');

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try {
      const json = await apiCall(`/api/admin/inroom/menu`);
      setData(json);
      setDrafts({});
    } catch (e) { setErr(e.message); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const uploadPhoto = async (sku, file) => {
    if (!file) return;
    setPhotoBusy(sku); setErr('');
    try {
      const fd = new FormData(); fd.append('file', file);
      const token = localStorage.getItem('admin_token');
      const csrf = document.cookie.split('; ').find((c) => c.startsWith('csrf_token='))?.split('=')[1];
      const res = await fetch(`${API_URL}/api/admin/inroom/menu/${sku}/photo`, { method: 'POST', body: fd, credentials: 'include', headers: { ...(token ? { Authorization: `Bearer ${token}` } : {}), ...(csrf ? { 'X-CSRF-Token': csrf } : {}) } });
      const json = await res.json();
      if (!res.ok) throw new Error(json.detail || 'Upload failed');
      await load();
    } catch (e) { setErr(e.message); } finally { setPhotoBusy(null); }
  };
  const removePhoto = async (sku) => {
    setPhotoBusy(sku); setErr('');
    try { await apiCall(`/api/admin/inroom/menu/${sku}/photo`, { method: 'DELETE' }); await load(); } catch (e) { setErr(e.message); } finally { setPhotoBusy(null); }
  };

  const translateMissing = async () => {
    setTranslating(true); setTransMsg(''); setErr('');
    try {
      const r = await apiCall('/api/admin/inroom/menu-translate', { method: 'POST', body: JSON.stringify({ only_missing: true }) });
      setTransMsg(r.updated ? `AI translated ${r.updated} item(s) into ${LANGS.join(' / ').toUpperCase()}.` : 'All items already have every guest language.');
      await load();
    } catch (e) { setErr(e.message); } finally { setTranslating(false); }
  };

  const startEdit = (it) => {
    setDrafts((d) => ({
      ...d,
      [it.sku]: {
        name: it.name, price: it.price, section: it.section,
        is_active: it.is_active, sort_order: it.sort_order,
      },
    }));
  };

  const cancelEdit = (sku) => {
    setDrafts((d) => { const n = { ...d }; delete n[sku]; return n; });
  };

  const saveEdit = async (sku) => {
    setSavingSku(sku);
    try {
      await apiCall(`/api/admin/inroom/menu/${sku}`, {
        method: 'PATCH', body: JSON.stringify(drafts[sku]),
      });
      await load();
    } catch (e) { alert(e.message); }
    finally { setSavingSku(null); }
  };

  const toggleActive = async (it) => {
    try {
      await apiCall(`/api/admin/inroom/menu/${it.sku}`, {
        method: 'PATCH', body: JSON.stringify({ is_active: !it.is_active }),
      });
      await load();
    } catch (e) { alert(e.message); }
  };

  const removeItem = async (sku) => {
    if (!window.confirm(`Delete "${sku}"?`)) return;
    try {
      await apiCall(`/api/admin/inroom/menu/${sku}`, { method: 'DELETE' });
      await load();
    } catch (e) { alert(e.message); }
  };

  const createNew = async () => {
    if (!nu.sku || !nu.name) { alert('SKU and name are required'); return; }
    try {
      await apiCall(`/api/admin/inroom/menu`, {
        method: 'POST', body: JSON.stringify(nu),
      });
      setNu({ sku: '', name: '', section: 'beverages', price: 0 });
      setShowNew(false);
      await load();
    } catch (e) { alert(e.message); }
  };

  const patchDraft = (sku, key, val) =>
    setDrafts((d) => ({ ...d, [sku]: { ...d[sku], [key]: val } }));

  const openTrans = (it) => setTransDlg({
    sku: it.sku,
    name: it.name,
    translations: { ...(it.translations || {}) },
  });

  const saveTranslations = async () => {
    if (!transDlg) return;
    try {
      await apiCall(`/api/admin/inroom/menu/${transDlg.sku}`, {
        method: 'PATCH',
        body: JSON.stringify({ translations: transDlg.translations }),
      });
      setTransDlg(null);
      await load();
    } catch (e) { alert(e.message); }
  };

  const items = data?.items || [];
  const sections = data?.sections || ['beverages', 'food', 'amenities'];

  // Hoist rows inline (no sub-component with props.map — Babel plugin quirk).
  const rows = items.map((it) => {
    const editing = drafts[it.sku] !== undefined;
    const d = drafts[it.sku];
    return (
      <tr key={it.sku} className="border-b last:border-b-0" data-testid={`menu-row-${it.sku}`}>
        <td className="py-2 pr-3 text-xs text-slate-500 font-mono">{it.sku}</td>
        {editing ? (
          <>
            <td className="py-2 pr-3">
              <input
                value={d.name}
                onChange={(e) => patchDraft(it.sku, 'name', e.target.value)}
                className="w-full text-sm border rounded px-2 py-1"
                data-testid={`menu-edit-name-${it.sku}`}
              />
            </td>
            <td className="py-2 pr-3">
              <select
                value={d.section}
                onChange={(e) => patchDraft(it.sku, 'section', e.target.value)}
                className="text-sm border rounded px-2 py-1"
              >
                {sections.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </td>
            <td className="py-2 pr-3 text-right">
              <input
                type="number"
                step="0.5"
                min="0"
                value={d.price}
                onChange={(e) => patchDraft(it.sku, 'price', parseFloat(e.target.value) || 0)}
                className="w-20 text-sm border rounded px-2 py-1 text-right"
                data-testid={`menu-edit-price-${it.sku}`}
              />
            </td>
            <td className="py-2 pr-3 text-right">
              <button
                onClick={() => saveEdit(it.sku)}
                disabled={savingSku === it.sku}
                className="p-1 rounded bg-green-500 hover:bg-green-600 text-white mr-1"
                data-testid={`menu-save-${it.sku}`}
              >
                {savingSku === it.sku ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              </button>
              <button onClick={() => cancelEdit(it.sku)} className="p-1 rounded bg-slate-200 hover:bg-slate-300">
                <X className="w-4 h-4" />
              </button>
            </td>
          </>
        ) : (
          <>
            <td className="py-2 pr-3 text-sm font-medium text-slate-800">{it.name}</td>
            <td className="py-2 pr-3 text-xs uppercase text-slate-500">{it.section}</td>
            <td className="py-2 pr-3 text-sm text-right font-bold text-orange-600">{eur(it.price)}</td>
            <td className="py-2 pr-3 text-right whitespace-nowrap">
              <button
                onClick={() => toggleActive(it)}
                className={`p-1 rounded ${it.is_active ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-100'}`}
                data-testid={`menu-toggle-${it.sku}`}
                title={it.is_active ? 'Active — click to hide' : 'Hidden — click to activate'}
              >
                {it.is_active ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
              </button>
              <button onClick={() => startEdit(it)} className="text-xs text-blue-600 hover:underline mx-2" data-testid={`menu-edit-${it.sku}`}>
                Edit
              </button>
              <label className="text-xs text-emerald-700 hover:underline mx-1 cursor-pointer inline-flex items-center gap-1" title={it.image_url ? 'Replace photo' : 'Add photo'} data-testid={`menu-photo-${it.sku}`}>
                {it.image_url ? <img src={`${API_URL}${it.image_url}`} alt="" className="w-7 h-7 rounded object-cover inline-block" /> : <ImagePlus className="w-4 h-4" />}
                {photoBusy === it.sku ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
                <input type="file" accept="image/*" className="hidden" disabled={photoBusy === it.sku} onChange={(e) => uploadPhoto(it.sku, e.target.files?.[0])} data-testid={`menu-photo-file-${it.sku}`} />
              </label>
              {it.image_url && <button onClick={() => removePhoto(it.sku)} className="text-[10px] text-slate-400 hover:text-red-600 mr-1" title="Remove photo" data-testid={`menu-photo-remove-${it.sku}`}>✕</button>}
              <button onClick={() => openTrans(it)} className="text-xs text-purple-600 hover:underline mx-1" data-testid={`menu-trans-${it.sku}`} title="Add or edit translations">
                🌐 {Object.keys(it.translations || {}).length}
              </button>
              <button onClick={() => removeItem(it.sku)} className="p-1 rounded text-red-500 hover:bg-red-50" data-testid={`menu-delete-${it.sku}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </td>
          </>
        )}
      </tr>
    );
  });

  const sectionOpts = sections.map((s) => <option key={s} value={s}>{s}</option>);

  return (
    <div data-testid="inroom-menu-tab">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <p className="text-xs text-slate-500">
          {items.length} item(s) · owners edit prices &amp; hide items live.
        </p>
        <div className="flex gap-2">
          <button
            onClick={translateMissing}
            disabled={translating}
            className="px-3 py-1.5 rounded-full border border-orange-300 text-orange-700 hover:bg-orange-50 text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
            data-testid="menu-translate-btn"
            title="Fill the missing ME / SQ / DE / RU / PL / HU names with AI"
          >
            {translating ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Languages className="w-3.5 h-3.5" />} AI translate missing
          </button>
          <button
            onClick={() => setShowNew((v) => !v)}
            className="px-3 py-1.5 rounded-full bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold flex items-center gap-1"
            data-testid="menu-new-btn"
          >
            <Plus className="w-3.5 h-3.5" /> New item
          </button>
          <button onClick={load} disabled={loading} className="text-slate-500 hover:text-slate-700 flex items-center gap-1 text-sm">
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Refresh
          </button>
        </div>
      </div>

      {err && <p className="text-sm text-red-600 mb-3">{err}</p>}
      {transMsg && <p className="text-sm text-emerald-700 mb-3" data-testid="menu-translate-msg">{transMsg}</p>}

      {showNew && (
        <div className="bg-orange-50 border border-orange-200 rounded-xl p-4 mb-4 grid grid-cols-1 md:grid-cols-5 gap-2 items-end" data-testid="menu-new-form">
          <label className="text-xs">
            <span className="block text-slate-600 mb-1">SKU (a-z, 0-9, _)</span>
            <input
              value={nu.sku}
              onChange={(e) => setNu({ ...nu, sku: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') })}
              placeholder="prosecco"
              className="w-full text-sm border rounded px-2 py-1.5"
              data-testid="menu-new-sku"
            />
          </label>
          <label className="text-xs md:col-span-2">
            <span className="block text-slate-600 mb-1">Name</span>
            <input
              value={nu.name}
              onChange={(e) => setNu({ ...nu, name: e.target.value })}
              placeholder="Prosecco (glass)"
              className="w-full text-sm border rounded px-2 py-1.5"
              data-testid="menu-new-name"
            />
          </label>
          <label className="text-xs">
            <span className="block text-slate-600 mb-1">Section</span>
            <select
              value={nu.section}
              onChange={(e) => setNu({ ...nu, section: e.target.value })}
              className="w-full text-sm border rounded px-2 py-1.5"
              data-testid="menu-new-section"
            >
              {sectionOpts}
            </select>
          </label>
          <label className="text-xs">
            <span className="block text-slate-600 mb-1">Price (€)</span>
            <div className="flex gap-2">
              <input
                type="number"
                step="0.5"
                min="0"
                value={nu.price}
                onChange={(e) => setNu({ ...nu, price: parseFloat(e.target.value) || 0 })}
                className="flex-1 text-sm border rounded px-2 py-1.5"
                data-testid="menu-new-price"
              />
              <button
                onClick={createNew}
                className="px-3 rounded bg-green-500 hover:bg-green-600 text-white text-sm font-semibold"
                data-testid="menu-new-save"
              >
                Add
              </button>
            </div>
          </label>
        </div>
      )}

      <div className="bg-white rounded-xl border overflow-x-auto">
        <table className="w-full">
          <thead className="text-xs uppercase text-slate-500 bg-slate-50">
            <tr className="border-b">
              <th className="text-left py-2 px-3">SKU</th>
              <th className="text-left py-2 px-3">Name</th>
              <th className="text-left py-2 px-3">Section</th>
              <th className="text-right py-2 px-3">Price</th>
              <th className="text-right py-2 px-3">Actions</th>
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>

      {transDlg && (
        <div
          className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
          onClick={() => setTransDlg(null)}
          data-testid="menu-trans-dlg"
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-xs uppercase tracking-widest text-slate-500 font-bold">Translations</p>
            <h3 className="text-lg font-bold text-slate-900 mt-1">{transDlg.name}</h3>
            <p className="text-xs text-slate-500 mb-4">Leave a field blank to fall back to the English name.</p>
            <div className="space-y-2">
              {LANGS.map((lg) => (
                <label key={lg} className="flex items-center gap-2">
                  <span className="text-xs font-bold text-slate-600 w-8 uppercase">{lg}</span>
                  <input
                    type="text"
                    value={transDlg.translations[lg] || ''}
                    onChange={(e) => setTransDlg((t) => ({
                      ...t,
                      translations: { ...t.translations, [lg]: e.target.value },
                    }))}
                    placeholder="—"
                    className="flex-1 text-sm border rounded px-2 py-1.5"
                    data-testid={`menu-trans-${lg}`}
                  />
                </label>
              ))}
            </div>
            <div className="flex gap-2 mt-4">
              <button
                onClick={saveTranslations}
                className="flex-1 py-2 rounded-lg bg-orange-500 hover:bg-orange-600 text-white text-sm font-semibold"
                data-testid="menu-trans-save"
              >
                Save translations
              </button>
              <button
                onClick={() => setTransDlg(null)}
                className="px-4 py-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 text-sm font-semibold"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InRoomMenuTab;
